import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";

const FEATURE_LABELS = {
  self_learning: "Pembelajaran lokal", ai_strategy: "Strategi lokal",
  auto_fishing: "Auto mancing", auto_verification: "Auto verifikasi",
  inventory_sync: "Sinkron inventory", material_management: "Kelola material",
  auto_craft: "Auto craft", auto_listing: "Auto listing",
  auto_transfer: "Auto transfer", collector_optimization: "Optimasi pengepul",
};
const METRIC_LABELS = {
  coins_per_hour: "Koin/jam", catch_per_hour: "Tangkapan/jam",
  material_per_hour: "Material/jam", rarity_rate: "Distribusi rarity",
  bait_consumption_per_hour: "Umpan/jam", downtime_percent: "Downtime %",
  failure_rate: "Tingkat gagal", verification_frequency: "Gangguan verifikasi",
};
const metricText = (metric) => {
  if (!metric || metric.status === "UNKNOWN" || metric.value == null) return "Belum terukur";
  if (typeof metric.value === "object") return Object.entries(metric.value)
    .map(([name, rate]) => `${name}: ${(rate * 100).toFixed(1)}%`).join(" · ") || "Belum terukur";
  return Number(metric.value).toLocaleString("id-ID", { maximumFractionDigits: 2 });
};
const BLOCKERS = {
  NEEDS_VERIFIED_RECIPE: "Resep Celestial Rod belum terverifikasi dari sumber game.",
  TARGET_INVENTORY_UNKNOWN_OR_STALE: "Inventory material akun target belum segar atau belum terbaca.",
  GLOBAL_INVENTORY_INCOMPLETE: "Inventory material beberapa akun owner belum lengkap.",
  REQUIREMENTS_NEED_ACCOUNT_CONFIRMATION: "Syarat akun harus diperiksa sebelum craft.",
  COST_NOT_FULLY_KNOWN: "Ada biaya yang belum diketahui.",
  COIN_RESERVE_NOT_CONFIGURED: "Cadangan minimum koin akun target belum diatur.",
  BALANCE_UNKNOWN: "Saldo koin akun target belum terbaca.",
  INSUFFICIENT_COINS_AFTER_RESERVE: "Saldo tidak cukup setelah cadangan minimum.",
};
const blockerText = (reason) => {
  const [code, item] = reason.split(":", 2);
  if (code === "TRANSFER_RULE_UNKNOWN_OR_DENIED") return `Aturan transfer ${item} belum terbukti atau tidak diizinkan.`;
  if (code === "INSUFFICIENT_KNOWN_SURPLUS") return `Surplus ${item} yang aman dipakai belum cukup.`;
  if (code === "TARGET_MATERIAL_UNKNOWN") return `Jumlah ${item} milik target belum diketahui.`;
  return BLOCKERS[reason] || reason;
};
const VIEWS = [
  ["overview", "Ringkasan"], ["strategy", "Strategy Learning"],
  ["knowledge", "Knowledge"], ["inventory", "Inventory"],
  ["planner", "Craft Planner"], ["experiments", "Experiments"],
  ["collector", "Collector"], ["listing", "Listing"],
  ["decisions", "Riwayat"], ["accounts", "Kontrol Akun"],
];
const displayDate = (value) => value ? new Date(value).toLocaleString("id-ID") : "Belum ada";

export default function OwnerAI() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = VIEWS.some(([key]) => key === searchParams.get("view")) ? searchParams.get("view") : "overview";
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState(null);
  const [inventory, setInventory] = useState(null);
  const [telemetry, setTelemetry] = useState(null);
  const [recentTelemetry, setRecentTelemetry] = useState([]);
  const [knowledge, setKnowledge] = useState(null);
  const [strategies, setStrategies] = useState(null);
  const [learningProfiles, setLearningProfiles] = useState(null);
  const [plan, setPlan] = useState(null);
  const [coinReserveDraft, setCoinReserveDraft] = useState({});
  const [materialReserveDraft, setMaterialReserveDraft] = useState({});
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncingAccountId, setSyncingAccountId] = useState(null);
  const [planningAccountId, setPlanningAccountId] = useState(null);
  const evidenceRequests = useRef({});
  const evidenceFetchedAt = useRef({});
  const load = useCallback(async () => {
    const response = await api.get("/owner-ai/overview");
    setOverview(response.data);
  }, []);
  const loadEvidence = useCallback(async (section, force = false) => {
    const requests = {
      inventory: () => api.get("/owner-ai/inventory").then(({ data }) => setInventory(data)),
      telemetry: () => api.get("/owner-ai/telemetry/summary").then(({ data }) => {
        setTelemetry(data.summary);
        setRecentTelemetry(data.recent || []);
      }),
      knowledge: () => api.get("/owner-ai/knowledge").then(({ data }) => setKnowledge(data.facts)),
      summary: () => api.get("/owner-ai/dashboard-summary").then(({ data }) => setSummary(data)),
      strategies: () => api.get("/owner-ai/strategies").then(({ data }) => setStrategies(data.strategies)),
      profiles: () => api.get("/owner-ai/learning/profiles").then(({ data }) => setLearningProfiles(data)),
    };
    const byView = {
      overview: ["summary"],
      strategy: ["summary", "telemetry", "profiles"],
      knowledge: ["knowledge", "inventory"],
      inventory: ["inventory", "knowledge"],
      planner: ["inventory", "knowledge", "summary"],
      experiments: ["strategies"],
      collector: ["summary"],
      listing: ["inventory"],
      decisions: ["summary"],
      accounts: ["inventory"],
      background: ["inventory", "knowledge", "strategies", "telemetry", "profiles"],
    };
    const fetchOne = (key) => {
      const running = evidenceRequests.current[key];
      if (running) return force ? running.then(() => fetchOne(key), () => fetchOne(key)) : running;
      if (!force && Date.now() - (evidenceFetchedAt.current[key] || 0) < 30000) return Promise.resolve();
      const job = requests[key]().then(() => { evidenceFetchedAt.current[key] = Date.now(); })
        .finally(() => { delete evidenceRequests.current[key]; });
      evidenceRequests.current[key] = job;
      return job;
    };
    const keys = byView[section] || byView.overview;
    const results = [];
    // Fill the other tabs in the background, two requests at a time.
    for (let start = 0; start < keys.length; start += section === "background" ? 2 : keys.length) {
      results.push(...await Promise.allSettled(keys.slice(start, start + (section === "background" ? 2 : keys.length)).map(fetchOne)));
    }
    if (section !== "background" && results.some((result) => result.status === "rejected")) {
      toast.warning("Sebagian data Owner AI belum berhasil dimuat. Coba buka bagian ini lagi.");
    }
    return results;
  }, []);
  useEffect(() => {
    load().catch(() => toast.error("Status Owner AI gagal dimuat"));
  }, [load]);
  useEffect(() => {
    loadEvidence(view);
  }, [view, loadEvidence]);
  useEffect(() => {
    const timer = window.setTimeout(() => { loadEvidence("background"); }, 700);
    return () => window.clearTimeout(timer);
  }, [loadEvidence]);

  const act = async (method, path, body, onSaved) => {
    setBusy(true);
    let response;
    try {
      response = await api[method](path, body);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Perubahan gagal");
      setBusy(false);
      return;
    }
    onSaved?.();
    try {
      await load();
    } catch {
      toast.warning("Perubahan tersimpan, tetapi tampilan belum terbarui. Coba buka ulang halaman.");
      setBusy(false);
      return;
    }
    setBusy(false);
    loadEvidence(view, true);
    const status = response.data?.automation_status || response.data?.status;
    const pending = {
      NEEDS_LOGIN: "Akun belum tersambung ke Telegram. Automation belum berjalan.",
      NEEDS_CONFIG: "Atur bot/grup automation akun ini dulu.",
      NEEDS_ACCESS: "Akses automation akun ini belum memenuhi syarat.",
      NEEDS_REVIEW: "Automation gagal dimulai. Periksa status akun.",
      GLOBAL_PAUSED: "Automation tetap berhenti karena kontrol global sedang OFF.",
    };
    if (response.data?.ok === false || pending[status]) toast.error(pending[status] || `Automation belum berjalan: ${status || "periksa status akun"}`);
    else if (path.endsWith("/resume-all") && response.data?.already_running) {
      toast.success("Automation sudah berjalan.");
    } else if (path.endsWith("/resume-all")) {
      const results = Object.values(response.data?.results || {});
      const resumed = results.filter((result) => result === "RESUMED" || result === "READY").length;
      toast[resumed === results.length ? "success" : "warning"](`${resumed}/${results.length} akun siap berjalan. Periksa status akun yang tertahan.`);
    } else if (path.endsWith("/resume") && status === "READY") {
      toast.success("Kendali manual selesai. Automation existing belum dijalankan untuk akun ini.");
    } else if (path.endsWith("/master") && body?.enabled && response.data?.stop_all) {
      toast.success("Master ON. Akun yang dihentikan lewat Stop All tetap perlu Resume All.");
    } else toast.success(status === "RESUMED" ? "Automation akun berjalan" : "Pengaturan tersimpan");
  };

  const syncInventory = async (accountId) => {
    setSyncingAccountId(accountId);
    try {
      const response = await api.post(`/owner-ai/inventory/${accountId}/sync`);
      loadEvidence("inventory", true);
      toast[response.data.status === "SYNCED" ? "success" : "error"](
        response.data.status === "SYNCED" ? "Inventory diperbarui" : "Inventory belum dapat dibaca");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Sinkronisasi gagal");
    } finally { setSyncingAccountId(null); }
  };

  const previewCelestial = async (accountId) => {
    setPlanningAccountId(accountId);
    setPlan(null);
    try {
      const response = await api.get(`/owner-ai/planner/celestial-rod/${accountId}`);
      setPlan(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Dry-run gagal dimuat");
    } finally { setPlanningAccountId(null); }
  };

  if (!overview) return <div className="p-6 text-slate-300">Memuat kontrol Owner AI…</div>;
  const settings = overview.settings || {};
  return (
    <div className="max-w-5xl mx-auto space-y-5 p-4 md:p-7" data-testid="owner-ai-panel">
      <div>
        <h1 className="text-2xl font-bold">Owner AI</h1>
        <p className="text-sm text-slate-400 mt-1">Panel privat untuk memantau data dan rencana lokal. Craft, transfer, dan listing Owner AI belum memiliki executor.</p>
      </div>
      <nav className="flex gap-2 overflow-x-auto pb-1" aria-label="Bagian Owner AI">
        {VIEWS.map(([key, label]) => <button key={key} onClick={() => setSearchParams({ view: key })}
          className={`shrink-0 rounded-lg border px-3 py-2 text-sm ${view === key ? "border-pink-400 bg-pink-500/15 text-white" : "border-white/10 text-slate-400"}`}>
          {label}
        </button>)}
      </nav>
      {view === "overview" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><h2 className="font-semibold">Kontrol global</h2><p className="text-xs text-slate-400">Stop All hanya berlaku untuk akun owner.</p></div>
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => act("put", "/owner-ai/master", { enabled: !settings.master_enabled })}
              className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-50">
              Master: {settings.master_enabled ? "ON" : "OFF"}
            </button>
            <button disabled={busy} onClick={() => act("put", "/owner-ai/self-learning", { enabled: settings.self_learning_enabled === false })}
              className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-50">
              Self Learning: {settings.self_learning_enabled === false ? "OFF" : "ON"}
            </button>
            <button disabled={busy} onClick={() => act("post", settings.stop_all ? "/owner-ai/resume-all" : "/owner-ai/stop-all")}
              className="rounded-lg bg-pink-600 px-3 py-2 text-sm font-semibold disabled:opacity-50">
              {settings.stop_all ? "Resume All" : "Stop All"}
            </button>
          </div>
        </div>
      </section>}
      {view === "overview" && <section className="space-y-3">
        <h2 className="font-semibold">Ringkasan yang dapat diperiksa</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
          {[
            ["Self Learning", summary ? `${summary.self_learning_enabled_accounts}/${summary.owner_account_count} akun ON` : "Belum dimuat", "accounts"],
            ["Akun teramati", summary?.learned_account_count ?? "Belum dimuat", "strategy"],
            ["Telemetry sessions", summary?.telemetry_sessions ?? "Belum dimuat", "strategy"],
            ["Knowledge facts", summary?.knowledge_facts ?? "Belum dimuat", "knowledge"],
            ["Strategy experiments", summary?.strategy_experiments ?? "Belum dimuat", "experiments"],
            ["Active / candidate", summary ? `${summary.strategy_active} / ${summary.strategy_candidates}` : "Belum dimuat", "experiments"],
            ["Global inventory", inventory ? `${inventory.accounts.length} akun · ${inventory.complete ? "segar" : "parsial"}` : "Belum dimuat", "inventory"],
            ["Craft goals", summary?.craft_goals?.length ?? "Belum dimuat", "planner"],
            ["Collector assignment", summary ? `${summary.collector_assignment.collector_count} pengepul · ${summary.collector_assignment.mapped_sources} sumber` : "Belum dimuat", "inventory"],
          ].map(([label, value, target]) => <button key={label} onClick={() => setSearchParams({ view: target })}
            className="rounded-xl border border-white/10 bg-white/5 p-3 text-left hover:border-pink-400/50">
            <div className="text-slate-400">{label}</div><div className="mt-1 font-semibold">{value}</div>
          </button>)}
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm space-y-1">
          <div>Terakhir telemetry: {displayDate(summary?.last_telemetry_at)}</div>
          <div>Terakhir evaluasi strategi: {displayDate(summary?.last_learning_update)}</div>
          <div className="text-xs text-slate-400">Penugasan pengepul berasal dari konfigurasi Pindah Koin existing; optimizer Owner AI belum aktif.</div>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm">
          <h3 className="font-semibold mb-2">Keputusan strategi terbaru</h3>
          {summary?.recent_decisions?.length ? summary.recent_decisions.map((decision) =>
            <p key={`${decision.strategy_id}:${decision.version}`} className="py-1">{decision.strategy_id} v{decision.version} · {decision.status} · {decision.reason || "alasan belum tersedia"}</p>)
            : <p className="text-slate-400">Belum ada evaluasi strategi. Tidak ada keputusan AI otomatis yang diklaim.</p>}
        </div>
      </section>}
      {view === "strategy" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
        <h2 className="font-semibold">Telemetry otomatis</h2>
        <p className="text-xs text-slate-400">Hanya hasil sesi akun owner yang diterima runner. Nilai yang tidak muncul dari sumber tetap tidak diketahui.</p>
        <p className="text-sm">Sesi tercatat: {summary?.telemetry_sessions ?? "belum dimuat"}. Ringkasan metrik memakai maksimal 200 sesi terbaru.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
          {Object.entries(METRIC_LABELS).map(([key, label]) => <div key={key} className="rounded-lg border border-white/10 p-3">
            <span className="text-slate-400">{label}</span><div>{metricText(telemetry?.metrics?.[key])}</div>
            <div className="text-xs text-slate-500">Cakupan: {telemetry?.metrics?.[key]?.covered_sessions ?? 0} sesi</div>
          </div>)}
        </div>
        <p className="text-xs text-amber-200">Perbandingan otomatis hanya muncul jika akun, bait, kondisi permainan, waktu, serta biaya nyata dapat dicocokkan. Hasilnya menunggu tinjauan; tidak ada promosi strategi otomatis.</p>
        <h3 className="font-semibold">Perbandingan hasil bersih</h3>
        {learningProfiles?.enabled && !learningProfiles.comparisons?.length && <p className="text-slate-400 text-sm">Belum ada dua pilihan rod dengan sampel dan biaya nyata yang cukup sebanding.</p>}
        {learningProfiles?.comparisons?.map((item) => <div key={`${item.account_id}:${item.bait}:${item.time_bucket}:${item.candidate_rod}:${item.baseline_rod}`} className="rounded-lg border border-white/10 p-3 text-sm">
          <div>{item.candidate_rod} vs {item.baseline_rod} · {item.bait}</div>
          <div>Hasil bersih/jam: {item.candidate_net_coins_per_hour} vs {item.baseline_net_coins_per_hour} koin · {item.shared_observation_days} hari sebanding</div>
          <div className="text-xs text-amber-200">Kandidat untuk ditinjau, belum otomatis diterapkan</div>
        </div>)}
        <h3 className="font-semibold">Profil hasil nyata per rod dan bait</h3>
        {!learningProfiles?.enabled && <p className="text-slate-400 text-sm">Self Learning sedang OFF. Telemetry tetap tercatat.</p>}
        {learningProfiles?.enabled && learningProfiles.profiles?.length === 0 && <p className="text-slate-400 text-sm">Belum ada sesi dengan rod dan bait yang sama-sama teramati.</p>}
        {learningProfiles?.profiles?.map((profile) => <div key={`${profile.account_id}:${profile.rod}:${profile.bait}:${profile.mode}:${profile.role}`} className="rounded-lg border border-white/10 p-3 text-sm">
          <div className="font-medium">{overview.accounts.find((account) => account.id === profile.account_id)?.label || profile.account_id} · {profile.rod} · {profile.bait}</div>
          <div>{profile.sessions} sesi · {profile.active_hours} jam aktif · {profile.observed_days} hari</div>
          <div>Tangkapan/jam: {profile.catch_per_hour == null ? "belum terukur" : metricText({ value: profile.catch_per_hour })} · Koin kotor/jam: {profile.coins_per_hour_gross == null ? "belum terukur" : metricText({ value: profile.coins_per_hour_gross })}</div>
          <div className="text-xs text-amber-200">{profile.status} · Belum dibandingkan karena kondisi dan biaya belum terverifikasi.</div>
        </div>)}
        <h3 className="font-semibold">Hasil fishing terbaru</h3>
        {recentTelemetry.length === 0 && <p className="text-slate-400 text-sm">Belum ada sesi yang tercatat otomatis.</p>}
        {recentTelemetry.map((event) => <div key={event.event_id} className="rounded-lg border border-white/10 p-3 text-sm">
          <div className="font-medium">{overview.accounts.find((account) => account.id === event.account_id)?.label || event.account_id} · {displayDate(event.observed_at)} · hasil sesi langsung</div>
          <div>{event.fields?.equipment_rod?.value || "Rod belum diketahui"} · {event.fields?.bait?.value || "Bait belum diketahui"} · {event.fields?.catch_count?.value ?? "?"} tangkapan</div>
          <div className="text-xs text-slate-400">Koin hasil sesi: {event.fields?.coins_reward?.status === "OBSERVED" ? event.fields.coins_reward.value : "belum diketahui"} · Durasi: {event.fields?.active_seconds?.value ?? "belum diketahui"} detik</div>
        </div>)}
      </section>}
      {(view === "knowledge" || view === "inventory" || view === "planner") && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
        <h2 className="font-semibold">{view === "knowledge" ? "Knowledge Base" : view === "planner" ? "Craft Planner · Celestial Rod" : "Global Inventory"}</h2>
        <p className="text-sm">Cakupan inventory: {inventory ? (inventory.complete ? "lengkap dan segar" : `${inventory.incomplete_accounts?.length ?? 0} akun belum lengkap/segar`) : "belum dimuat"}</p>
        <p className="text-sm">Fakta game bersumber: {knowledge?.length ?? "belum dimuat"}. Resep Celestial Rod: {knowledge?.find((item) => item.kind === "RECIPE" && item.fact_key === "celestial_rod")?.status || "belum tervalidasi"}.</p>
        <p className="text-xs text-slate-400">Pembaruan inventory bersifat baca saja. Fakta yang bertentangan ditahan; resep dan biaya tidak ditebak.</p>
        {view === "knowledge" && <div className="space-y-2">
          {(knowledge || []).map((item) => <div key={`${item.kind}:${item.fact_key}`} className="rounded-lg border border-white/10 p-3 text-sm">
            <div className="font-medium">{item.kind} · {item.fact_key}</div>
            <div className="text-slate-400">{item.status} · {item.source_type || "sumber belum ada"} · {displayDate(item.last_observed_at)}</div>
            <div className="text-xs text-slate-500">Referensi: {item.source_reference || "belum ada"}</div>
          </div>)}
          {knowledge?.length === 0 && <p className="text-slate-400">Belum ada fakta game yang tercatat.</p>}
        </div>}
        {(view === "inventory" || view === "planner") && <div className="space-y-2">
          {(inventory?.accounts || []).map((account) => <div key={account.account_id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-white/10 p-3 text-sm">
            <div><div>{account.label || account.account_id} · {account.status}</div><div className="text-xs text-slate-400">{account.observed_at ? `Terakhir dibaca: ${displayDate(account.observed_at)}` : "Belum disinkronkan"}</div>
              {view === "inventory" && <div className="text-xs text-slate-300 mt-1">{account.items?.length || 0} jenis item · {account.coverage?.materials || "UNKNOWN"} material</div>}
            </div>
            <div className="flex flex-wrap gap-2">
              <button disabled={syncingAccountId !== null} onClick={() => syncInventory(account.account_id)} className="rounded-lg border border-white/20 px-3 py-2 disabled:opacity-50">{syncingAccountId === account.account_id ? "Memperbarui…" : "Perbarui inventory"}</button>
              {view === "planner" && <button disabled={planningAccountId !== null} onClick={() => previewCelestial(account.account_id)} className="rounded-lg border border-sky-400/50 px-3 py-2 disabled:opacity-50">{planningAccountId === account.account_id ? "Menyiapkan…" : "Dry-run Celestial Rod"}</button>}
            </div>
            {view === "inventory" && !!account.items?.length && <div className="w-full grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-slate-300">
              {account.items.slice(0, 30).map((item) => <div key={item.item_key} className="rounded border border-white/10 p-2">{item.name}: dimiliki {item.owned}, terkunci {item.locked}, cadangan {item.reserved}, pending {item.pending}, tersedia {item.available ?? "UNKNOWN"}</div>)}
            </div>}
          </div>)}
        </div>
        }
        {view === "planner" && !!summary?.craft_goals?.length && <p className="text-xs text-slate-400">Target tersimpan: {summary.craft_goals.map((item) => `${item.account_id}: ${item.goal}`).join(" · ")}</p>}
        {view === "planner" && plan && <div className="rounded-xl border border-sky-400/30 p-4 space-y-2 text-sm" data-testid="celestial-dry-run">
          <h3 className="font-semibold">Dry-run Celestial Rod · {plan.target_account_id}</h3>
          <p>Status: {plan.status}. Tidak ada transfer atau craft yang dijalankan.</p>
          <p>Biaya koin diketahui: {plan.known_coin_cost == null ? "belum lengkap" : plan.known_coin_cost.toLocaleString("id-ID")}</p>
          {!!plan.requirements?.length && <p>Syarat game yang masih perlu diperiksa: {plan.requirements.join(", ")}</p>}
          {plan.materials?.map((item) => <p key={item.item_key}>{item.item_key}: butuh {item.required}, tersedia {item.target_available}, kurang {item.shortage}, belum terpenuhi {item.unfilled}</p>)}
          {plan.proposed_transfers?.map((item, index) => <p key={`${item.from_account_id}-${item.item_key}-${index}`}>Usulan: {item.quantity} {item.item_key} dari {item.from_account_id} ke {item.to_account_id}</p>)}
          {!!plan.blockers?.length && <div><div className="font-medium text-amber-300">Alasan tertahan</div><ul className="list-disc pl-5">{plan.blockers.map((reason) => <li key={reason}>{blockerText(reason)}</li>)}</ul></div>}
          {!!plan.unknown_cost_components?.length && <p>Biaya belum diketahui: {plan.unknown_cost_components.join(", ")}</p>}
        </div>}
      </section>}
      {view === "experiments" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
        <h2 className="font-semibold">Experiments</h2>
        <p className="text-xs text-slate-400">Status VALIDATED adalah kandidat hasil evaluasi, bukan izin promosi ACTIVE.</p>
        {(strategies || []).map((item) => <div key={`${item.strategy_id}:${item.version}`} className="rounded-lg border border-white/10 p-3 text-sm space-y-1">
          <div className="font-semibold">{item.strategy_id} v{item.version} · {item.status}</div>
          <div className="text-slate-400">{item.hypothesis}</div>
          <div>Akun uji: {item.test_accounts?.join(", ") || "belum ada"} · Batas sesi: {item.max_sessions ?? "UNKNOWN"}</div>
          <div>Hasil: {item.result?.reason || "Belum dievaluasi"}</div>
          {item.result?.candidate && <div>Sampel: {item.result.candidate.samples} · Jam aktif: {item.result.candidate.active_hours?.toFixed(2)} · Hari: {item.result.candidate.distinct_days}</div>}
        </div>)}
        {strategies?.length === 0 && <p className="text-slate-400">Belum ada eksperimen strategi.</p>}
      </section>}
      {view === "collector" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3 text-sm">
        <h2 className="font-semibold">Collector Management</h2>
        <p>{summary?.collector_assignment?.collector_count ?? "–"} akun pengepul · {summary?.collector_assignment?.mapped_sources ?? "–"} akun sumber terpetakan.</p>
        <p className="text-slate-400">Penugasan berasal dari pengaturan Pindah Koin yang sudah ada. Perbandingan fee lintas collector belum mempunyai data terverifikasi.</p>
        <Link to="/dashboard/coin-transfer" className="inline-block rounded-lg border border-white/20 px-3 py-2">Buka pengaturan pengepul</Link>
      </section>}
      {view === "listing" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3 text-sm">
        <h2 className="font-semibold">Strategi Listing</h2>
        <p className="text-slate-400">Owner AI belum menjalankan listing. Strategi harga dan fee menunggu hasil transaksi game yang teramati; tidak ada harga pasar yang ditebak.</p>
        {(inventory?.accounts || []).map((account) => <p key={account.account_id}>{account.label}: {account.market_active_listings == null ? "listing belum terbaca" : `${account.market_active_listings} listing aktif teramati`}</p>)}
      </section>}
      {view === "decisions" && <section className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3 text-sm">
        <h2 className="font-semibold">Decision / History AI</h2>
        {summary?.recent_decisions?.length ? summary.recent_decisions.map((decision) => <div key={`${decision.strategy_id}:${decision.version}`} className="rounded-lg border border-white/10 p-3">
          {decision.strategy_id} v{decision.version} · {decision.status} · {decision.reason || "alasan belum tersedia"} · {displayDate(decision.updated_at)}
        </div>) : <p className="text-slate-400">Belum ada keputusan strategi dari data terukur.</p>}
      </section>}
      {view === "accounts" && <section className="space-y-3">
        <h2 className="font-semibold">Akun pribadi</h2>
        <label className="block text-sm text-slate-300">Pilih akun
          <select aria-label="Pilih akun" value={selectedAccountId || String(overview.accounts[0]?.id || "")}
            onChange={(event) => setSelectedAccountId(event.target.value)}
            className="mt-2 w-full rounded-lg border border-white/20 bg-slate-900 px-3 py-2">
            {overview.accounts.map((account) => <option key={account.id} value={String(account.id)}
              label={`${account.label} · ${account.owner_ai?.takeover ? "diambil alih" : account.owner_ai?.master_enabled ? "ON" : "OFF"}`} />)}
          </select>
        </label>
        {overview.accounts.filter((account) => String(account.id) === (selectedAccountId || String(overview.accounts[0]?.id))).map((account) => {
          const config = account.owner_ai;
          return <div key={account.id} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-4">
            <div className="flex flex-wrap justify-between gap-3">
              <div><h3 className="font-semibold">{account.label}</h3><p className="text-xs text-slate-400">{config ? `${config.mode} · ${config.takeover ? "diambil alih" : "otomatis"}` : "Pengaturan Owner AI belum diinisialisasi"}</p>
                <p className="text-xs text-slate-400 mt-1">Automation existing: {account.existing_automation_enabled ? "ON" : "OFF"} · Status: {account.automation_state?.status || "belum ada"} · Verifikasi: {account.automation_state?.verification_status || "belum ada"}</p>
                <p className="text-xs text-slate-400">Equipment: {inventory?.accounts.find((item) => item.account_id === account.id)?.loadout?.rod || "belum terbaca"} · Bait: {inventory?.accounts.find((item) => item.account_id === account.id)?.loadout?.bait || "belum terbaca"}</p>
              </div>
              {config && <div className="flex gap-2">
                {config.takeover
                  ? <button disabled={busy} onClick={() => act("post", `/owner-ai/accounts/${account.id}/resume`)}
                    className="rounded-lg border border-emerald-400/50 px-3 py-2 text-sm disabled:opacity-50">Resume</button>
                  : <button disabled={busy} onClick={() => act("post", `/owner-ai/accounts/${account.id}/take-control`)}
                    className="rounded-lg border border-amber-400/50 px-3 py-2 text-sm disabled:opacity-50">Take Control</button>}
              </div>}
            </div>
            {config && <>
              <div className="flex flex-wrap gap-2 items-center">
                <button disabled={busy} onClick={() => act("patch", `/owner-ai/accounts/${account.id}`, { master_enabled: !config.master_enabled })}
                  className="rounded-lg border border-white/20 px-3 py-2 text-sm disabled:opacity-50">Akun: {config.master_enabled ? "ON" : "OFF"}</button>
                <label className="text-sm">Mode <select value={config.mode} disabled={busy || config.takeover}
                  onChange={(event) => act("patch", `/owner-ai/accounts/${account.id}`, { mode: event.target.value })}
                  className="ml-2 rounded-lg border border-white/20 bg-slate-900 px-2 py-2">
                  <option>MANUAL</option><option>ASSISTED</option><option>FULL_AUTO</option>
                </select></label>
              </div>
              <div className="space-y-2 rounded-lg border border-white/10 p-3 text-sm">
                <label className="flex flex-wrap items-center gap-2">Cadangan minimum koin
                  <input type="number" min="0" disabled={busy}
                    value={coinReserveDraft[account.id] ?? config.minimum_coin_reserve ?? ""}
                    onChange={(event) => setCoinReserveDraft((old) => ({ ...old, [account.id]: event.target.value }))}
                    className="w-32 rounded-lg border border-white/20 bg-slate-900 px-2 py-1" />
                  <button disabled={busy || coinReserveDraft[account.id] === undefined || coinReserveDraft[account.id] === ""}
                    onClick={() => act("patch", `/owner-ai/accounts/${account.id}`,
                      { minimum_coin_reserve: Number(coinReserveDraft[account.id]) },
                      () => setCoinReserveDraft((old) => {
                        const next = { ...old }; delete next[account.id]; return next;
                      }))}
                    className="rounded-lg border border-white/20 px-3 py-1 disabled:opacity-50">Simpan</button>
                </label>
                {(inventory?.accounts.find((item) => item.account_id === account.id)?.items || [])
                  .filter((item) => item.category === "material").map((item) => {
                    const draftKey = `${account.id}:${item.item_key}`;
                    const draft = materialReserveDraft[draftKey];
                    return <label key={draftKey} className="flex flex-wrap items-center gap-2">Simpan {item.name}
                      <input type="number" min="0" disabled={busy} value={draft ?? config.material_reserves?.[item.item_key] ?? 0}
                        onChange={(event) => setMaterialReserveDraft((old) => ({ ...old, [draftKey]: event.target.value }))}
                        className="w-24 rounded-lg border border-white/20 bg-slate-900 px-2 py-1" />
                      <button disabled={busy || draft === undefined || draft === ""}
                        onClick={() => act("patch", `/owner-ai/accounts/${account.id}`, {
                          material_reserves: { ...config.material_reserves, [item.item_key]: Number(draft) },
                        }, () => setMaterialReserveDraft((old) => {
                          const next = { ...old }; delete next[draftKey]; return next;
                        }))}
                        className="rounded-lg border border-white/20 px-3 py-1 disabled:opacity-50">Simpan</button>
                    </label>;
                  })}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => <label key={key} className="flex items-center gap-2 text-sm text-slate-300">
                  <input type="checkbox" checked={Boolean(config.features?.[key])} disabled={busy}
                    onChange={() => act("patch", `/owner-ai/accounts/${account.id}`, { features: { ...config.features, [key]: !config.features[key] } })} />{label}
                </label>)}
              </div>
              <p className="text-xs text-amber-200/80">Auto-verification reguler tetap mengikuti hak paket dan service existing. Toggle Owner AI untuk fitur ini belum terhubung; craft, listing, dan transfer juga belum dieksekusi oleh Owner AI.</p>
            </>}
          </div>;
        })}
      </section>}
    </div>
  );
}
